import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Brain, Target, BookOpen } from 'lucide-react';

interface GeneratingPopupProps {
  isOpen: boolean;
}

export function GeneratingPopup({ isOpen }: GeneratingPopupProps) {
  const messages = [
    { icon: Brain, text: "Analyzing your learning goals..." },
    { icon: Target, text: "Crafting personalized milestones..." },
    { icon: BookOpen, text: "Structuring your learning path..." },
    { icon: Sparkles, text: "Adding expert recommendations..." }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 flex items-center justify-center p-4 z-50"
          >
            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl mx-auto relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-blue-400/5 rounded-2xl"></div>
              <div className="relative p-6 sm:p-8">
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full blur-xl opacity-25 animate-pulse"></div>
                    <div className="relative bg-gradient-to-br from-blue-600 to-blue-500 p-3 rounded-2xl">
                      <Brain className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </div>
                
                <h3 className="text-xl sm:text-2xl font-semibold text-center text-gray-900 mb-2">
                  Generating Your Roadmap
                </h3>
                <p className="text-gray-600 text-center text-sm sm:text-base mb-8">
                  Please wait while we curate your personalized learning journey
                </p>

                <div className="space-y-4">
                  {messages.map((message, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.5 }}
                      className="flex items-center gap-3 text-gray-700 bg-gray-50/50 p-3 rounded-lg"
                    >
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <message.icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="flex-1 text-sm sm:text-base font-medium">{message.text}</span>
                      <motion.div
                        animate={{ opacity: [1, 0] }}
                        transition={{ duration: 1, repeat: Infinity, delay: index * 0.3 }}
                        className="w-2 h-2 rounded-full bg-blue-600"
                      />
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}